<script>
    import CardRecommendation from "./CardRecommendation.svelte";
    import LazyLoad from "vanilla-lazyload";
    import { onMount, setContext } from "svelte";
    import { calculateTileDimension } from "../utils/tileDimensionCalculator.js";
    import { key as cinemaTileDimensionCtxKey } from "../context/cinemaTileDimension.js";
    import { emitMiniActionEvent } from "../tracking/eventEmitter.js";
    import scrollTracker from "../tracking/scrollTracker.js";
    import viewTracker from "../tracking/viewTracker.js";
    import { generateRandomId } from "../utils/IdentifierGenerator.js";

    /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */

    /*                */
    let {
        headline = null,
        recommendations = [],
        cinemaInjectionContainerWidth,
        sourceVariationId = undefined,
        featuresState = {},
        featureIndex = undefined,
        cinemaIndex = undefined,
        cinemaMeta = null,
        additionalNextPiTrackingLabels = null,
        additionalTrackingLabels = null,
        additionalClickTracking = null,
        tracking = null,
        initialTrackingStatus = "loaded",
        personalisedUser = false,
        site = null,
        hasElevationLevel = false,
        cinemaContainerMounted = Promise.resolve(),
    } = $props();

    const initialVisibleTiles = 12;
    let alreadyTrackedIndexTiles = $state([]);
    let visibleNodeTiles = $state(initialVisibleTiles);

    const { width, height, numberOfVisibleTiles, minimumHeight, minimumWidth } = $derived(
        calculateTileDimension(cinemaInjectionContainerWidth)
    );
    let tileDimensions = $state({
        get width() {
            return width;
        },
        get height() {
            return height;
        },
        get numberOfVisibleTiles() {
            return numberOfVisibleTiles;
        },
        get minimumWidth() {
            return minimumWidth;
        },
        get minimumHeight() {
            return minimumHeight;
        },
    });
    setContext(cinemaTileDimensionCtxKey, tileDimensions);
    const recoCardCinemaClassToUse = headline ? "reco_card_cinema__with_headline" : "reco_card_cinema__no_headline";

    let cardCinemaFeature;
    const cinemaId = `reco_cinema__${generateRandomId(16)}`;
    let visibilityPreviousActionAlreadyTracked = false;
    const hasHeadline = ![null, ""].includes(headline);

    const scrollTracking = (event) => {
        if (event.target.id !== cinemaId) return;

        if (event.detail.scrollDirection !== "right") {
            if (!visibilityPreviousActionAlreadyTracked) {
                trackVisibilityHorizontalScroll("previous");
                visibilityPreviousActionAlreadyTracked = true;
            }
        } else {
            const indicesIncludingPartialTile = event.detail.visibleItems.indices.filter(
                (index) => !alreadyTrackedIndexTiles.includes(index)
            );
            const tilesFeatureIds = indicesIncludingPartialTile.map((index) => recommendations[index]?.tracking?.id);
            if (tilesFeatureIds.length > 0) {
                trackVisibilityHorizontalScroll("next", tilesFeatureIds);
                alreadyTrackedIndexTiles = [...alreadyTrackedIndexTiles, ...indicesIncludingPartialTile];
            }
        }
    };

    const trackView = (recommendations, numberOfVisibleTiles) => {
        const recommendationsTracking = recommendations.slice(0, numberOfVisibleTiles + 1).map((recommendation) => recommendation.tracking);

        viewTracker(
            tracking,
            recommendationsTracking,
            featureIndex,
            cinemaIndex,
            null,
            additionalTrackingLabels,
            null,
            initialTrackingStatus
        );
    };

    const trackVisibilityVerticalScroll = (recommendations, numberOfVisibleTiles) => {
        const { id: cinemaFeatureId } = tracking;
        const tilesFeatureIds = recommendations.slice(0, numberOfVisibleTiles + 1).map((recommendation) => recommendation.tracking.id);
        const featureIds = [cinemaFeatureId, ...tilesFeatureIds];
        emitMiniActionEvent(featureIds);
    };

    const trackVisibilityHorizontalScroll = (direction, tilesFeatureIds = []) => {
        const visibleRecos = recommendations.filter((reco) => tilesFeatureIds.includes(reco.tracking?.id));
        const recommendationsTracking = visibleRecos.map(({ tracking }) => tracking);
        const recommendationsMeta = visibleRecos.map(({ meta }) => meta);
        scrollTracker(
            tracking,
            recommendationsTracking,
            featureIndex,
            cinemaIndex,
            direction,
            cinemaMeta,
            recommendationsMeta,
            sourceVariationId,
            additionalTrackingLabels
        );
    };

    const lazyLoadConfig = {
        unobserve_entered: true,
        threshold: 0,
        callback_loaded: (el) => {
            el.parentElement.nextElementSibling.firstElementChild.classList.remove("p_loader250");
        },
        callback_error: (el) => {
            el.parentElement.nextElementSibling.firstElementChild.classList.remove("p_loader250");
        },
    };

    const lazyLoadImages = () => {
        if ("loading" in HTMLImageElement.prototype) {
            const images = document.querySelectorAll(".reco_card_cinema__image .lazy");
            images.forEach((img) => {
                img.parentElement.nextElementSibling.firstElementChild.classList.remove("p_loader250");
                img.src = img.dataset.src;
            });
        } else {
            const images = document.querySelectorAll(".reco_card_cinema__image .lazy");
            const lazyLoader = new LazyLoad(lazyLoadConfig, images);
            lazyLoader.update();
        }
    };

    cinemaContainerMounted.then(() => {
        const visibleTiles = parseInt(window.getComputedStyle(document.getElementById(cinemaId)).getPropertyValue("--items-per-page") || 2);
        alreadyTrackedIndexTiles = [...Array(visibleTiles + 1).keys()];

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (visibleNodeTiles < recommendations.length) {
                        requestAnimationFrame(() => {
                            visibleNodeTiles = recommendations.length;
                        });
                    }
                    trackVisibilityVerticalScroll(recommendations, visibleTiles);
                    observer.unobserve(entry.target);
                }
            });
        });
        observer.observe(cardCinemaFeature);
        trackView(recommendations, visibleTiles);
    });

    onMount(() => {
        lazyLoadImages();
        const cinema = document.querySelector(`#${cinemaId}`);

        cinema.addEventListener("oc-stage-scroll-completed", (event) => scrollTracking(event));
    });
</script>

<div bind:this={cardCinemaFeature} class="{recoCardCinemaClassToUse} {site}">
    {#if hasHeadline}
        <h2 class="oc-ml-100 oc-pb-100 oc-headline-100 oc-headline-200--l">{headline}</h2>
    {/if}
    <oc-cinema-v1
        id={cinemaId}
        gap="8px"
        resizing="cut"
        arrow-button-position="{tileDimensions.height / 2}px"
        class="reco_card_cinema__carousel"
        stage-spacing="16px"
        item-visibility-threshold="0.01"
        class:reco_card_cinema__carousel_no_margin={!hasHeadline}
    >
        {#each recommendations as recommendation, index}
            {#if index < visibleNodeTiles}
                <div
                    class="reco_card_cinema_tile_wrapper"
                    class:reco_card_cinema_tile_wrapper_no_headline_first_child={!hasHeadline && index === 0}
                >
                    <CardRecommendation
                        {recommendation}
                        lazyLoad={index > 4}
                        {featuresState}
                        cinemaTracking={tracking}
                        {featureIndex}
                        {cinemaIndex}
                        {cinemaMeta}
                        {additionalClickTracking}
                        {hasElevationLevel}
                        {additionalNextPiTrackingLabels}
                        {additionalTrackingLabels}
                        {personalisedUser}
                    />
                </div>
            {/if}
        {/each}
    </oc-cinema-v1>
</div>

<style lang="scss">.reco_card_cinema__no_headline {
  padding: 16px 0 16px;
  height: 100%;
  width: 100%;
  line-height: normal;
  container-type: inline-size;
}

.reco_card_cinema__with_headline {
  padding: 0 0 24px;
  height: 100%;
  width: 100%;
  line-height: normal;
  container-type: inline-size;
}

.reco_card_cinema__carousel {
  font-size: 0;
  --items-per-page: 2;
}
.reco_card_cinema__carousel_no_margin {
  margin-left: 0 !important;
}

@container (width > 767px) {
  .reco_card_cinema__carousel {
    --items-per-page: 5;
  }
}
@container ((width > 636px) and (width <= 767px)) {
  .reco_card_cinema__carousel {
    --items-per-page: 4;
  }
}
@container ((width >= 497px) and (width <= 636px)) {
  .reco_card_cinema__carousel {
    --items-per-page: 3;
  }
}
@container (width < 497px) {
  .reco_card_cinema__carousel {
    --items-per-page: 2;
  }
}
.reco_card_cinema_tile_wrapper_no_headline_first_child {
  margin-left: 16px;
}</style>
